import { useMemo } from 'react';
import { useTaskStore } from '../stores/taskStore';
import { useHabitStore } from '../stores/habitStore';
import { useEventStore } from '../stores/eventStore';
import { useProjectStore } from '../stores/projectStore';
import { useQuestStore } from '../stores/questStore';
import { useAbilitiesStore } from '../stores/abilitiesStore';
import { useProgramStore } from '../stores/programStore';
import { useRoutineStore } from '../stores/routineStore';
import {
  activeItemsByDomain, capacityStatus, completionCounts,
  type CapacitySnapshot, type CapacityStatus,
} from '../utils/capacityCore';
import type { ActionTypeId } from '../constants/achievementBadges';

export type { CapacityStatus, ActiveItem } from '../utils/capacityCore';

/**
 * The REACTIVE half of the capacity reading — subscribes to the nine stores so
 * a surface re-renders when a holding changes.
 *
 * ⚠️ The maths itself lives in `utils/capacityCore.ts` and is shared with the
 * imperative create guard. Do not reimplement a predicate here: the bar and the
 * block must never be able to disagree about what "active" means.
 */
function useCapacitySnapshot(): CapacitySnapshot {
  const tasksByDomain = useTaskStore(s => s.tasks);
  const habits = useHabitStore(s => s.habits);
  const events = useEventStore(s => s.events);
  const projects = useProjectStore(s => s.projects);
  const quests = useQuestStore(s => s.quests);
  const abilities = useAbilitiesStore(s => s.abilities);
  const programs = useProgramStore(s => s.programs);
  const initiations = useProgramStore(s => s.initiations);
  const routines = useRoutineStore(s => s.routines);

  return useMemo(
    () => ({ tasksByDomain, habits, events, projects, quests, abilities, programs, initiations, routines }),
    [tasksByDomain, habits, events, projects, quests, abilities, programs, initiations, routines],
  );
}

/**
 * Returns a reader: `status(type, domainId, parentId?)`.
 *
 * ⭐ A FUNCTION, NOT A MAP, because Goals and Skills need a parent to answer at
 * all and a map would have to pre-compute every quest and ability whether or not
 * one is on screen. Callers ask about the one row they are rendering.
 *
 * ⚠️ Returns `null` when the answer would be a guess — an unknown domain, or a
 * per-parent type asked without a parent. A zero-shaped placeholder there would
 * render a full, wrong bar.
 */
export function useCapacityStatus() {
  const snap = useCapacitySnapshot();

  return useMemo(() => {
    const active = activeItemsByDomain(snap);
    const completions = completionCounts(snap);
    return (type: ActionTypeId, domainId: string, parentId?: string): CapacityStatus | null =>
      capacityStatus(snap, active, completions, type, domainId, parentId);
  }, [snap]);
}
